/**
 * Practice Plan Trial Modal
 *
 * Team signups (org size > "Just me") with Practice plan trial — practice-variant
 * sibling of ClinicianTrialModal. Queue selection lives in launchModalsConfigAtom;
 * BE grants CLINICIAN_PRO_PLUS + team on first non-solo org-size write, so this
 * modal only dismisses (no activate call).
 *
 * Primary CTA may open PracticeInviteTeamModal for legacy `/` self-onboarding
 * (no InviteTeamStep). V2 marks invite-step done in sessionStorage and skips
 * that chain — CTAs collapse to single "Got it" with practice icon.
 *
 * Figma: https://www.figma.com/design/edgIeajMOqpAxGbdqcq2oZ/Sign-up---onboarding?node-id=9645-4022&m=dev
 */

import {
  PRACTICE_INVITE_ACTION,
  PRACTICE_TRIAL_ACTION,
  TRIALS_AND_UPGRADES_SCREEN,
} from '@heidi/commerce/contracts';
import { Button } from '@heidi/design-system/button';
import { ModalId } from '@heidi/schema/ml-scribe';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { getCommerceBillingUiPorts } from '../../settings/billing/billing-ui-ports';
import { practiceInviteTeamModalOpenAtom } from '../practice-invite-team-modal/practice-invite-team-modal-atoms';
import { getCommerceTrialsUiPorts } from '../trials-ui-ports';
import { practiceTrialModalMessages } from './practice-trial-modal.messages';

export const PracticeTrialModal = () => {
  const intl = useIntl();
  const { isOpen, handleClose, dismissible } = getCommerceTrialsUiPorts().useQueuedModal(
    ModalId.PRACTICE_TRIAL
  );
  const setPracticeInviteTeamModalOpen = useSetAtom(practiceInviteTeamModalOpenAtom);
  const inviteTeamStepCompleted = getCommerceTrialsUiPorts().useInviteTeamStepCompleted();
  const setPracticeTrialModalPending =
    getCommerceTrialsUiPorts().useSetPracticeTrialModalPending();

  const { practiceTrialStartFeatures } = getCommerceBillingUiPorts().useFeatureLists();
  const TrialStartModalBase = getCommerceTrialsUiPorts().TrialStartModalBase;

  // Same gate as closeModal → invite chain. Invite done in onboarding ⇒ single
  // dismiss CTA; legacy `/` still gets invite + skip pair.
  const willOpenInviteModal =
    !!getCommerceTrialsUiPorts().isPracticeInviteFlowEnabled() &&
    !inviteTeamStepCompleted;

  useEffect(() => {
    if (isOpen) {
      getCommerceTrialsUiPorts().analytics.trackPracticeTrialViewed({
        screen_name: TRIALS_AND_UPGRADES_SCREEN.practiceTrial,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Trial already activated by BE on org-size PATCH — close is a no-op for billing.
  const closeModal = useCallback(
    (openInviteFlow: boolean) => {
      setPracticeTrialModalPending(false);
      handleClose();
      if (openInviteFlow && willOpenInviteModal) {
        setPracticeInviteTeamModalOpen(true);
      }
    },
    [
      handleClose,
      setPracticeInviteTeamModalOpen,
      setPracticeTrialModalPending,
      willOpenInviteModal,
    ]
  );

  const primaryCtaMessage = willOpenInviteModal
    ? practiceTrialModalMessages.cta
    : practiceTrialModalMessages.gotIt;

  const handlePrimaryClick = useCallback(() => {
    getCommerceTrialsUiPorts().analytics.trackPracticeTrialClick({
      screen_name: TRIALS_AND_UPGRADES_SCREEN.practiceTrial,
      action: PRACTICE_TRIAL_ACTION.getStarted,
      label: intl.formatMessage(primaryCtaMessage),
    });
    closeModal(willOpenInviteModal);
  }, [closeModal, intl, primaryCtaMessage, willOpenInviteModal]);

  const handleTryOnMyOwn = useCallback(() => {
    // Reports the INVITE modal's skip token, not one of this modal's own — the
    // two CTAs are the same funnel step. See
    // `practice-trial-invite-analytics.types.ts`.
    getCommerceTrialsUiPorts().analytics.trackPracticeTrialClick({
      screen_name: TRIALS_AND_UPGRADES_SCREEN.practiceTrial,
      action: PRACTICE_INVITE_ACTION.skip,
      label: intl.formatMessage(practiceTrialModalMessages.tryOnMyOwn),
    });
    closeModal(false);
  }, [closeModal, intl]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setPracticeTrialModalPending(false);
        handleClose();
      }
    },
    [handleClose, setPracticeTrialModalPending]
  );

  if (getCommerceBillingUiPorts().isAppleCompliantBuild()) {
    return null;
  }

  return (
    <TrialStartModalBase
      open={isOpen}
      onOpenChange={handleOpenChange}
      dismissible={dismissible}
      hideCloseIcon
      variant="practice"
      headerIcon="gift"
      title={intl.formatMessage(practiceTrialModalMessages.title)}
      subtitle={intl.formatMessage(practiceTrialModalMessages.subtitle)}
      features={practiceTrialStartFeatures ?? []}
      primaryButtonText={intl.formatMessage(primaryCtaMessage)}
      footerText={intl.formatMessage(practiceTrialModalMessages.footerText)}
      hidePrimaryButtonIcon={willOpenInviteModal}
      onPrimaryClick={handlePrimaryClick}
      secondaryButton={
        willOpenInviteModal ? (
          <Button
            className="w-full"
            variant="outline"
            size="lg"
            onClick={handleTryOnMyOwn}
          >
            <FormattedMessage {...practiceTrialModalMessages.tryOnMyOwn} />
          </Button>
        ) : undefined
      }
    />
  );
};
